import { useEffect, useRef, useState, type ReactNode, type MouseEvent } from 'react';
import { getDocument, GlobalWorkerOptions, version as pdfjsVersion, type PDFDocumentProxy } from 'pdfjs-dist';

// Raw file from pdfWorkerPlugin (dev middleware + dist root). Vite ?url injects
// /@vite/client into the worker module and pdf.js type:module workers die.
GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.js?v=${pdfjsVersion}`;

interface Props {
  url: string;
  onPageClick?: (pageNumber: number, x: number, y: number) => void;
  onPageRect?: (pageNumber: number, x0: number, y0: number, x1: number, y1: number) => void;
  overlay?: (pageNumber: number) => ReactNode;
  placeMode?: boolean;
}

export function PdfDocumentViewer({ url, onPageClick, overlay, placeMode, onPageRect }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.floor(el.clientWidth));
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    const raf = requestAnimationFrame(measure);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let doc: PDFDocumentProxy | null = null;
    setPdf(null);
    setNumPages(0);
    setError(null);

    getDocument({ url }).promise
      .then((loaded) => {
        if (cancelled) {
          void loaded.cleanup();
          return;
        }
        doc = loaded;
        setNumPages(loaded.numPages);
        setPdf(loaded);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Impossible de lire le PDF');
        }
      });

    return () => {
      cancelled = true;
      void doc?.cleanup();
    };
  }, [url]);

  return (
    <div ref={wrapRef} className="w-full">
      {error && <p className="text-sm text-red-600 py-8 text-center">{error}</p>}
      {!pdf && !error && (
        <p className="text-sm text-cime-text-muted py-8 text-center">Chargement du document…</p>
      )}
      {pdf && width <= 0 && !error && (
        <p className="text-sm text-cime-text-muted py-8 text-center">Chargement du document…</p>
      )}
      {pdf && width > 0 &&
        Array.from({ length: numPages }, (_, i) => (
          <PdfPage
            key={i + 1}
            pdf={pdf}
            pageNumber={i + 1}
            width={width}
            onPageClick={onPageClick}
            onPageRect={onPageRect}
            overlay={overlay}
            placeMode={placeMode}
          />
        ))}
    </div>
  );
}

function PdfPage({
  pdf,
  pageNumber,
  width,
  onPageClick,
  onPageRect,
  overlay,
  placeMode,
}: {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  onPageClick?: (pageNumber: number, x: number, y: number) => void;
  onPageRect?: (pageNumber: number, x0: number, y0: number, x1: number, y1: number) => void;
  overlay?: (pageNumber: number) => ReactNode;
  placeMode?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const skipClickRef = useRef(false);
  const [drag, setDrag] = useState<{ x: number; y: number; cx: number; cy: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0) return;
    let cancelled = false;
    let renderTask: { cancel: () => void } | null = null;

    pdf.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      const unscaled = page.getViewport({ scale: 1 });
      const scale = width / unscaled.width;
      const viewport = page.getViewport({ scale });
      const outputScale = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      renderTask = page.render({ canvas, canvasContext: ctx, viewport });
    });

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, width]);

  function norm(e: MouseEvent<HTMLDivElement>): { x: number; y: number } | null {
    const r = e.currentTarget.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  }

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    if (!onPageClick) return;
    const p = norm(e);
    if (!p) return;
    onPageClick(pageNumber, p.x, p.y);
  }

  function handleDown(e: MouseEvent<HTMLDivElement>) {
    if (!onPageRect) return;
    const p = norm(e);
    if (!p) return;
    dragRef.current = p;
    setDrag({ ...p, cx: p.x, cy: p.y });
  }

  function handleMove(e: MouseEvent<HTMLDivElement>) {
    if (!dragRef.current || !onPageRect) return;
    const p = norm(e);
    if (!p) return;
    setDrag({ x: dragRef.current.x, y: dragRef.current.y, cx: p.x, cy: p.y });
  }

  function handleUp(e: MouseEvent<HTMLDivElement>) {
    if (!onPageRect || !dragRef.current) return;
    const start = dragRef.current;
    const p = norm(e) ?? start;
    onPageRect(pageNumber, start.x, start.y, p.x, p.y);
    skipClickRef.current = true;
    dragRef.current = null;
    setDrag(null);
  }

  const preview = drag
    ? {
        left: `${Math.min(drag.x, drag.cx) * 100}%`,
        top: `${Math.min(drag.y, drag.cy) * 100}%`,
        width: `${Math.abs(drag.cx - drag.x) * 100}%`,
        height: `${Math.abs(drag.cy - drag.y) * 100}%`,
      }
    : null;

  return (
    <div
      className={`relative overflow-visible bg-white shadow-sm mb-4 ${placeMode || onPageRect ? 'cursor-crosshair' : ''}`}
      data-page={pageNumber}
      onClick={handleClick}
      onMouseDown={handleDown}
      onMouseMove={handleMove}
      onMouseUp={handleUp}
      onMouseLeave={() => {
        dragRef.current = null;
        setDrag(null);
      }}
    >
      <canvas ref={canvasRef} className="block" />
      {preview && (
        <div className="absolute bg-amber-400/40 border border-amber-500 pointer-events-none" style={preview} />
      )}
      {overlay?.(pageNumber)}
    </div>
  );
}
